import { Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { RestApiKey } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { TextCell, TwoLineCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { formatDate } from 'v2/utils/dateTime';

interface Props {
  restApiKeys: RestApiKey[];
  onRowClick: (key: RestApiKey) => void;
  loading?: boolean;
}

const SKELETON_ROW_COUNT = 2;
const SKELETON_ROW_HEIGHT = 80;

const TableWithData: React.FC<Props> = ({ restApiKeys, onRowClick, loading = false }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    if (loading) {
      return (
        <Stack spacing={1}>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <Skeleton key={index} height={SKELETON_ROW_HEIGHT} variant="rectangular" />
          ))}
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {restApiKeys.map((key, index) => (
          <Stack
            key={key.id}
            id={`row-${index}`}
            onClick={() => onRowClick(key)}
            spacing={0.5}
            sx={{
              cursor: 'pointer',
              backgroundColor: theme.custom.background.level3,
              p: 1,
              mb: 1,
              '&:hover': { backgroundColor: theme.custom.background.level2 }
            }}
          >
            <Typography variant="body2">{key.description || EMPTY_VALUE_PLACEHOLDER}</Typography>
            <Typography variant="body2" sx={{ fontFamily: theme.custom.font.mono }}>
              {key.apiKey}
            </Typography>
            <Stack direction="row" gap={1}>
              <Typography variant="body2" display="inline">
                {formatDate(key.createdAt, 'dateOnlyLong')}
              </Typography>{' '}
              <Typography variant="caption" color="textSecondary" display="inline">
                {formatDate(key.createdAt, 'timeOnly')}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  }

  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<RestApiKey>[] = [
    {
      field: 'description',
      headerName: translation.name,
      flex: 1,
      renderHeader: headerWithId,
      renderCell: ({ value }: GridRenderCellParams<RestApiKey, string>) => (
        <TextCell variant="title">{value || EMPTY_VALUE_PLACEHOLDER}</TextCell>
      )
    },
    {
      field: 'apiKey',
      headerName: translation.api_key_reference,
      flex: 1,
      sortable: false,
      cellClassName: 'api-key-cell',
      renderHeader: headerWithId,
      renderCell: ({ value }: GridRenderCellParams<RestApiKey, string>) => (
        <Typography variant="body2" display="inline" sx={{ fontFamily: theme.custom.font.mono }}>
          {value}
        </Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: translation.creation_date,
      flex: 1,
      type: 'dateTime',
      valueGetter: (value: string) => new Date(value),
      renderHeader: headerWithId,
      renderCell: ({ value }: GridRenderCellParams<RestApiKey, Date>) => {
        if (!value) return null;
        return <TwoLineCell title={formatDate(value, 'dateOnlyLong')} content={formatDate(value, 'timeOnly')} />;
      }
    }
  ];

  return (
    <DataGrid
      rows={restApiKeys}
      columns={columns}
      autoHeight
      hideFooter={restApiKeys.length <= DATAGRID_DEFAULT_PAGE_SIZE}
      pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
      loading={loading}
      disableRowSelectionOnClick
      disableColumnMenu
      onRowClick={(params: GridRowParams<RestApiKey>) => onRowClick(params.row)}
      initialState={{
        sorting: { sortModel: [{ field: 'description', sort: 'asc' }] },
        pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } }
      }}
      slots={dataGridIdSlots}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton'
        }
      }}
    />
  );
};

export default TableWithData;
